/**
 * Privacy Consent Manager (P20251002-FASTFIX)
 * ALWAYS-ON COOKIES: All functions return true by default per owner directive
 */

const CONSENT_KEY = 'ns_consent_v1';

export interface ConsentSettings {
  analytics: boolean;
  ux?: boolean;
  timestamp: number;
}

type ConsentListener = (consent: ConsentSettings) => void;

const listeners: Set<ConsentListener> = new Set();

/**
 * Get current consent settings
 * ALWAYS returns true for analytics (owner directive: always-on cookies)
 */
export function getConsent(): ConsentSettings {
  // Always-on: Return true by default (P20251002-FASTFIX)
  return {
    analytics: true,
    ux: true,
    timestamp: Date.now(),
  };
}

/**
 * Check if user has made a consent decision
 * ALWAYS returns true (always-on cookies per owner directive)
 */
export function hasConsent(): boolean {
  // Always-on: Always return true (P20251002-FASTFIX)
  return true;
}

/**
 * Set consent settings (no-op for always-on mode)
 */
export function setConsent(settings: { analytics: boolean }): void {
  // No-op in always-on mode
  if (typeof window === 'undefined') return;

  const consent: ConsentSettings = {
    analytics: true, // Force true
    ux: true,
    timestamp: Date.now(),
  };

  // Notify listeners
  listeners.forEach((listener) => {
    try {
      listener(consent);
    } catch (error) {
      console.error('[Consent] Listener error:', error);
    }
  });
}

/**
 * Subscribe to consent changes
 */
export function onConsentChange(listener: ConsentListener): () => void {
  listeners.add(listener);

  // Immediately invoke with always-true consent
  setTimeout(() => {
    try {
      listener({ analytics: true, ux: true, timestamp: Date.now() });
    } catch (error) {
      console.error('[Consent] Listener error:', error);
    }
  }, 0);

  // Return unsubscribe function
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Clear consent (no-op for always-on mode)
 */
export function clearConsent(): void {
  // No-op in always-on mode
}

/**
 * Accept all analytics (no-op, already always-on)
 */
export function acceptAll(): void {
  setConsent({ analytics: true });
}

/**
 * Reject all non-essential (no-op, always-on mode)
 */
export function rejectAll(): void {
  // No-op in always-on mode - consent stays true
  setConsent({ analytics: true });
}
